import {
  CartFlagType,
  CartFlags,
  CartFlagsService,
  CartUtils,
  ProductCategory,
  RetrieveCartResponse,
  SessionStorageKeys,
  RetrieveCartItem,
  Discount
} from '@aglenergy/shared-library';
import { Component, ComponentInterface, Host, JSX, Prop, h } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { BundleSearchStore } from 'app/shared/models/current-search-request.model';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { SelectedProductDetails, SelectedProductSessionData } from 'app/shared/models/sessionData';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { locationService } from 'app/shared/services/location/location-service';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';

@Component({
  tag: 'bundle-summary',
  styleUrl: 'bundle-summary.scss',
  shadow: true
})
export class BundleSummary implements ComponentInterface {
  @Prop() public buttonText = 'Go to Cart';
  @Prop() public cartDetails: RetrieveCartResponse;
  private monthlyTotalDiscounts = 0;
  private cartFlags: CartFlags;
  private cartFlagsService;
  private bundleFlags: string;
  private authenticatedProducts: BundleSearchStore;

  public navigateToNext(): void {
    const selectedMobileProducts: SelectedProductSessionData = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails)
    );
    const selectedNbnProducts: any = JSON.parse(sessionStorage.getItem(SessionStorageKeys.SelectedNBNProductDetails));
    const selectedEnergyProducts: CustomerProfile = JSON.parse(sessionStorage.getItem(SessionStorageKeys.CustomerProfile));
    const tealiumContext = ['Bundle Builder][Go to Cart][Clicked'];
    if (selectedMobileProducts) {
      const planNames: string = selectedMobileProducts.productDetails
        .map((productDetails: SelectedProductDetails) => productDetails.product.name)
        .toString();
      tealiumAnalyticService.sendEvent(
        tealiumContext,
        null,
        ProductCategory.Mobile,
        { product_plan_in_the_cart: planNames },
        'mobile plan'
      );
    }

    if (selectedNbnProducts) {
      const planName: string = selectedNbnProducts.productDetails.product.name;
      tealiumAnalyticService.sendEvent(
        tealiumContext,
        null,
        ProductCategory.Nbn,
        { product_plan_in_the_cart: planName },
        ProductCategory.Nbn
      );
    }
    if (selectedEnergyProducts) {
      let category = '';
      if (selectedEnergyProducts.plans?.electricity && selectedEnergyProducts.plans?.gas) {
        category = 'Elec, Gas';
      } else if (selectedEnergyProducts.plans?.electricity) {
        category = 'Elec';
      } else if (selectedEnergyProducts.plans?.gas) {
        category = 'Gas';
      }

      const planName = selectedEnergyProducts.planFeatureDisclaimer?.title;
      tealiumAnalyticService.sendEvent(tealiumContext, null, category, { product_plan_in_the_cart: planName }, ProductCategory.Energy);
    }

    if (selectedMobileProducts) {
      locationService.navigateToEligibility('mobile plan', 'B100175');
    } else {
      locationService.navigateToUrl('/cart?cidi=A11097');
    }
  }

  public render(): JSX.Element {
    this.setCartFlags();
    return (
      <Host>
        <div class="bundle-summary">{this.cartDetails?.cartItems?.length ? this.getBundleSummary() : this.getEmptyBundleSummary()}</div>
      </Host>
    );
  }

  private getBundleSummary(): JSX.Element {
    return (
      <div>
        <div
          {...(this.cartFlags.hasBundleProductCategories
            ? { class: 'bundle-summary__info bundled-category' }
            : { class: 'bundle-summary__info' })}
        >
          {this.getBundleInfo()}
        </div>
        <div class="bundle-summary__cta">
          <agl-ds-button type="primary" sr-context={this.buttonText} onClick={this.navigateToNext}>
            {this.buttonText}
          </agl-ds-button>
        </div>
      </div>
    );
  }

  private getBundleInfo(): JSX.Element {
    let monthlyTotalSubText: string;
    let highlightMonthlyTotalSavings = this.cartFlags.hasBundleProductCategories;
    const monthlyTotal = this.getMonthlyTotal();
    if (store.get('isAuthenticated') && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true')) {
      this.getAuthDiscounts();
    }
    let monthlyTotalSavings = 'Add energy plan & save';

    const cartFlag = this.bundleFlags ? this.bundleFlags : this.getCartFlag();
    switch (cartFlag) {
      case CartFlagType.hasEnergyMobileSimNbnBundle:
        monthlyTotalSavings = `$${this.monthlyTotalDiscounts} off internet & mobile plans`;
        monthlyTotalSubText = '(internet & mobile) + energy';
        break;
      case CartFlagType.hasEnergyMobileSimBundle:
        monthlyTotalSavings = `$${this.monthlyTotalDiscounts} off mobile plan`;
        monthlyTotalSubText = '(mobile) + energy';
        break;
      case CartFlagType.hasEnergyNbnBundle:
        monthlyTotalSavings = `$${this.monthlyTotalDiscounts} off internet plan`;
        monthlyTotalSubText = '(internet) + energy';
        break;
      case CartFlagType.hasMobileSimNbnBundle:
        highlightMonthlyTotalSavings = false;
        monthlyTotalSubText = '(internet & mobile)';
        break;
      case CartFlagType.hasMobileSimOnly:
        monthlyTotalSubText = '(mobile)';
        break;
      case CartFlagType.hasNbnOnly:
        monthlyTotalSubText = '(internet)';
        break;
      case CartFlagType.hasEnergyOnly:
      default:
        break;
    }

    return (
      <div class="bundle-summary__content">
        {
          <bundle-summary-discount
            highlightMonthlyTotalSavings={highlightMonthlyTotalSavings}
            monthlyTotal={cartFlag !== CartFlagType.hasEnergyOnly ? `$${monthlyTotal}` : 'Based on energy usage'}
            monthlyTotalSavings={monthlyTotalSavings}
            monthlyTotalSubText={monthlyTotalSubText}
          />
        }
      </div>
    );
  }

  private getEmptyBundleSummary(): JSX.Element {
    return (
      <div class="hidden-sm hidden-md visible-lg empty-bundle">
        <div class="empty-bundle__info">
          <agl-ds-text styled-as="xl">Bundle your services and start saving.</agl-ds-text>
        </div>
      </div>
    );
  }

  private getCartFlag(): string {
    return Object.entries(this.cartFlags)
      .filter(
        ([key, value]) =>
          ![CartFlagType.hasBundleProductCategories, CartFlagType.hasEnergy, CartFlagType.hasMobileSim, CartFlagType.hasNbn].includes(
            key as CartFlagType
          ) && value
      )
      .map(([key]) => key)
      ?.shift();
  }

  private getMonthlyTotal(): number {
    this.monthlyTotalDiscounts = 0;
    return [...this.cartDetails.cartItems]
      .filter(
        (cartItem) =>
          !!cartItem?.additionalData?.productDisplayAttributes &&
          ![ProductCategory.CarbonNeutral, ProductCategory.Energy, ProductCategory.Modem].includes(
            cartItem.additionalData.productCategory.toLowerCase() as ProductCategory
          )
      )
      .map((cartItem) => {
        const productDisplayAttributes = cartItem.additionalData.productDisplayAttributes as Partial<{ price: string }>;
        const nonCouponDiscount = CartUtils.getPromotionDiscount(cartItem.productDiscounts, false);
        if (nonCouponDiscount) {
          this.monthlyTotalDiscounts += CartUtils.normaliseDiscountAmount(nonCouponDiscount.discountAmount);
        }
        return CartUtils.calculateDiscountedPrice(Number(productDisplayAttributes.price), nonCouponDiscount?.discountAmount);
      })
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
  }

  private getAuthDiscounts(): void {
    const selectedProducts: RetrieveCartItem[] = this.cartDetails.cartItems;
    this.authenticatedProducts = store.get(StoreKeys.bundleSearchStore);
    if (this.authenticatedProducts?.address) {
      this.monthlyTotalDiscounts = 0;
      this.addBundleFlag('clear');
      selectedProducts?.map((cartItem: RetrieveCartItem) => {
        const item = cartItem.additionalData?.productCategory.toLowerCase();
        this.authenticatedProducts.discounts?.map((discount: Discount) => {
          if (item.includes(discount.productCategory.applyTo)) {
            this.monthlyTotalDiscounts += discount.discountAmount ? +discount.discountAmount : 0;
            this.addBundleFlag(discount.productCategory.applyTo.toLowerCase());
          }
        });
      });
    }
  }

  private addBundleFlag(productCategory?: string): void {
    switch (productCategory) {
      case 'mobile plan':
        if (this.bundleFlags.includes('hasEnergyNbnBundle') || this.bundleFlags.includes('hasEnergyMobileSimNbnBundle')) {
          this.bundleFlags = 'hasEnergyMobileSimNbnBundle';
        } else {
          this.bundleFlags = 'hasEnergyMobileSimBundle';
        }
        break;
      case 'nbn':
        if (this.bundleFlags.includes('hasEnergyMobileSimBundle') || this.bundleFlags.includes('hasEnergyMobileSimNbnBundle')) {
          this.bundleFlags = 'hasEnergyMobileSimNbnBundle';
        } else {
          this.bundleFlags = 'hasEnergyNbnBundle';
        }
        break;
      case 'clear':
        this.bundleFlags = '';
        break;
      default:
        break;
    }
  }

  private setCartFlags(): void {
    if (this.cartDetails) {
      this.cartFlagsService = new CartFlagsService(this.cartDetails);
      this.cartFlags = this.cartFlagsService.getCartFlags();
    }
  }
}
